import { Injectable } from '@angular/core';
import {
  map,
  filter,
  tap,
  withLatestFrom,
  take,
  mergeMap,
  delay
} from 'rxjs/operators';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';

import { zefGo } from '@zerops/zef/ngrx-router';
import {
  authActions,
  selectZefAuthAccessToken,
  requires2FA
} from '@zerops/zef/auth';
import { AuthTokenStorageService } from '@zerops/zef/auth';
import { FEATURE_NAME_ZEF_AUTH } from '@zerops/zef/auth/auth.constant';
import { zefGithubLoginSuccess, zefGithubSignupSuccess } from '@zerops/zef/github';
import { zefGitlabLoginSuccess, zefGitlabSignupSuccess } from '@zerops/zef/gitlab';
// TODO: fix import ??
import {
  zefWebsocketClosed,
  zefWebsocketTerminate
} from '@zerops/zef/websocket/websocket.action';
import { zefWebsocketAuth, ZefWebsocketService } from '@zerops/zef/websocket';
import { zefResetState } from '@zerops/zef/core';
import { Noola } from '@zerops/zef/noola';
import { requestRecipeAdd } from '@zerops/zui/recipes';
import { getRecipeById } from '@zerops/models/recipes';
import { RecipeBaseActions, recipeBaseState, buildRecipeSourceUrl, scanNullSecrets } from '@zerops/zerops/core/recipe-base';
import {
  loadStoredUserDataSuccess,
  loadUser,
  clearUserData,
  loadStoredUserData,
  UserEntity,
  storeUserDataSuccess,
  setUserId
} from '@zerops/zerops/core/user-base';
import { loadSettings } from '@zerops/zerops/core/settings-base';
import { loadProjectTags, projectImport, queueProjectImport } from '@zerops/zerops/core/project-base';
import { consumeRecipeHandoff } from '@zerops/zerops/core/recipe-handoff-base';
import { selectZagentConfigMap } from '@zerops/zerops/pages/+recipe/pages/list/recipe-list.state';
import { clearPersistedTerminalTabs } from '@zerops/zerops/feature/terminal-tabs/terminal-tabs.util';
import { pendingDeployConfigActions } from './pending-deploy-config.state';
import { AppState } from './app.model';

@Injectable()
export class AppEffect {

  private _onAnyAuthSuccess$ = this._actions$.pipe(ofType(
    authActions.setToken,
    loadStoredUserDataSuccess
  ));

  private _onAnyGithubGitlabAuthSuccess$ = this._actions$.pipe(
    ofType(
      zefGithubLoginSuccess,
      zefGithubSignupSuccess,
      zefGitlabLoginSuccess,
      zefGitlabSignupSuccess
    )
  );

  private _onZefSetToken$ = this._actions$.pipe(ofType(authActions.setToken));

  private _onZefCheckStoredTokenSuccess$ = this._actions$.pipe(ofType(
    authActions.checkStoredTokenSuccess
  ));

  private _onAnyAuthClear$ = this._actions$.pipe(ofType(authActions.logout, authActions.checkStoredTokenFail, authActions.refreshTokenFail));

  private _onCheckStoredTokenSuccessSetUserId$ = createEffect(() => this._onZefCheckStoredTokenSuccess$.pipe(
    map(({ userId }) => setUserId(userId)),
  ));

  private _onAnyAuthSuccessGenerateReceiverId$ = createEffect(() => this._onZefSetToken$.pipe(
    filter(({ data }) => !requires2FA(data)),
    tap(() => this._wService.generateReceiverId())
  ), { dispatch: false });

  /**
   * Load user data and stored user preferences after token validation succeeds.
   *
   * IMPORTANT: This effect only triggers for fully verified tokens.
   * Partial 2FA tokens are filtered out to prevent:
   * 1. Failed API calls with unverified tokens
   * 2. Null activeClientUserId causing permission guard failures
   * 3. Blank pages when user refreshes during 2FA flow
   *
   * For partial tokens, the token validation will fail (isValid() returns false),
   * triggering checkStoredTokenFail which redirects to login page where
   * the user can complete 2FA authentication.
   */
  private _onCheckStoredTokenSuccess$ = createEffect(() => this._onZefCheckStoredTokenSuccess$.pipe(
    // Filter out partial 2FA tokens - they should never reach here due to
    // isValid() check, but this is a defensive guard in case token state
    // changes after validation (e.g., concurrent tab operations)
    filter((tokenData) => !requires2FA(tokenData)),
    mergeMap(() => [
      loadUser(),
      loadStoredUserData()
    ])
  ));

  /**
   * It is necessary to distinguish a special case, when the logout action is used
   * for clearing the state and notify BE before processing the /authorize route.
   * It is used mainly when opening different clients through the BackOffice application.
   */
  onLogoutRedirect$ = createEffect(() => this._onAnyAuthClear$.pipe(
    filter(() => !this._tokenStorage.sessionInvalidated()),
    map((action) => {
      if (action.type === `[${FEATURE_NAME_ZEF_AUTH}] Logout`) {
        if (action?.meta?.redirect === false) {
          return authActions.noopAction();
        }
      }
      return zefGo([ '/login' ]);
    })
  ));

  // Cross-tab session invalidation: run full cleanup immediately so periodic
  // effects (timers, intervals) stop before any 401 cascade can happen.
  // The modal (signal-based) survives zefResetState.
  // NOTE: Do NOT dispatch clearUserData() here — it clears shared localStorage
  // which the switching tab needs when it reloads to complete the org switch.
  // Logout cleanup is handled separately by _onLogoutClearUser$.
  private _onCrossTabSessionInvalidated$ = createEffect(() =>
    this._tokenStorage.crossTabSessionInvalidated$.pipe(
      tap(() => this._noola.shutdown()),
      mergeMap(() => [
        zefWebsocketTerminate(),
        zefResetState()
      ])
    )
  );

  private _onLogoutResetState$ = createEffect(() => this._onAnyAuthClear$.pipe(
    map(() => zefResetState())
  ));

  private _onLogoutWebsocketTerminate$ = createEffect(() => this._onAnyAuthClear$.pipe(
    map(() => zefWebsocketTerminate())
  ));

  private _onLogoutCleanupNoola$ = createEffect(() => this._onAnyAuthClear$.pipe(
    tap(() => this._noola.shutdown())
  ), { dispatch: false });

  private _onActiveClientLoadSettings$ = createEffect(() => this._userEntity.activeClientId$.pipe(
    map((clientId) => loadSettings(clientId))
  ));

  private _onAnyAuthSuccessLoadProjectTags$ = createEffect(() => this._userEntity.activeClientId$.pipe(
    map((clientId) => loadProjectTags(clientId))
  ));

  private _onSetTokenLoadUser$ = createEffect(() => this._onZefSetToken$.pipe(
    filter(({ data }) => !requires2FA(data)),
    map(({ meta }) => loadUser({ meta }))
  ));

  private _onLogoutClearUser$ = createEffect(() => this._onAnyAuthClear$.pipe(
    map(() => clearUserData())
  ));

  private _onLogoutClearTerminalTabs$ = createEffect(() => this._onAnyAuthClear$.pipe(
    tap(() => clearPersistedTerminalTabs())
  ), { dispatch: false });

  // websockets auth triggers
  private _onZefSetTokenAuthWebsockets$ = createEffect(() => this._onZefSetToken$.pipe(
    filter(({ data }) => !requires2FA(data)),
    map(({ data: { accessToken }}) => zefWebsocketAuth({
      token: accessToken,
      receiverId: this._wService.getReceiverId()
    }, { type: 'noop' }))
  ));

  private _onZefCheckStoredSuccessAuthWebsockets$ = createEffect(() => this._onZefCheckStoredTokenSuccess$.pipe(
    // needed to allow forFeature websocket effect to register
    delay(0),
    map(({ accessToken }) => zefWebsocketAuth({ token: accessToken, receiverId: this._wService.getReceiverId() }, { type: 'noop' }))
  ));

  private _onZefWsClosed$ = createEffect(() => this._actions$.pipe(
    ofType(zefWebsocketClosed),
    filter(() => !this._tokenStorage.sessionInvalidated()),
    withLatestFrom(this._store.pipe(select(selectZefAuthAccessToken))),
    filter(([ _, token ]) => !!token),
    map(([ _, token ]) => zefWebsocketAuth({ token, receiverId: this._wService.getReceiverId() }, { type: 'noop' }))
  ));

  private _onRequestRecipeAdd$ = createEffect(() => this._actions$.pipe(
    ofType(requestRecipeAdd),
    map(({ yaml, id }) => projectImport({ data: { yaml }, meta: { tag: id } }))
  ));

  // DEPRECATED: strapi recipes
  // private _onQueueProjectImport$ = createEffect(() => this._actions$.pipe(
  //   ofType(queueProjectImport),
  //   mergeMap((action) => this._userEntity.activeClientId$.pipe(
  //     filter((d) => !!d),
  //     take(1),
  //     switchMap(() => this._recipesRervice
  //       .selectById$(action.nonHaRecipeId || action.haRecipeId)
  //       .pipe(
  //         filter((d) => !!d),
  //         take(1),
  //         map((data) => projectImport(action.nonHaRecipeId ? data.attributes.config_nonha : data.attributes.config_ha))
  //       )
  //     )
  //   ))
  // ));

  // Shared stream for parsing queue import actions
  private _queueImportWithClientId$ = this._actions$.pipe(
    ofType(queueProjectImport),
    mergeMap((action) => this._userEntity.activeClientId$.pipe(
      filter((d) => !!d),
      take(1),
      map(() => action)
    ))
  );

  // Effect 1: Handle old recipe system or trigger new recipe loading
  private _onQueueProjectImport$ = createEffect(() => this._queueImportWithClientId$.pipe(
    mergeMap((action) => {
      try {
        // Try to parse as enhanced recipe format
        const recipeData = JSON.parse(action.nonHaRecipeId || '{}');

        // Handoff path takes precedence — consume the encrypted payload from
        // backend, which re-dispatches queueProjectImport without the handoff
        // field once it has the resolved data + zagentConfig populated. The
        // second pass falls through to the normal recipe-load logic below.
        if (recipeData.handoff) {
          return [consumeRecipeHandoff({ id: recipeData.handoff })];
        }

        if (recipeData.recipeSlug && recipeData.environmentName) {

          if (recipeData.type === 'github') {
            // GitHub recipe - check if loaded, otherwise load by URL
            const githubUrl = recipeData.githubUrl;
            if (!githubUrl) return [];

            return this._store.pipe(
              select(recipeBaseState.selectGitHubRecipeByUrl(githubUrl)),
              take(1),
              mergeMap((githubRecipe) => {
                if (githubRecipe) {
                  // GitHub recipe already loaded
                  return [];
                } else {
                  // Load GitHub recipe
                  return [RecipeBaseActions.loadGithubRecipe({
                    url: githubUrl,
                    specificYaml: recipeData.environmentName
                  })];
                }
              })
            );
          } else {
            // Strapi recipe - existing logic
            return this._store.pipe(
              select(recipeBaseState.selectRecipeBySlug(recipeData.recipeSlug)),
              take(1),
              mergeMap((recipe) => {
                if (recipe) {
                  // Recipe already loaded - let the second effect handle import
                  return [];
                } else {
                  // Recipe not loaded - dispatch load action
                  return [RecipeBaseActions.loadRecipe({ id: recipeData.recipeSlug })];
                }
              })
            );
          }
        }
      } catch (e) {
        // Not JSON, fall back to old system
      }

      // Old recipe system - static data lookup
      const recipe = getRecipeById(action.nonHaRecipeId);
      if (!recipe) { return []; }
      return [projectImport({
        yaml: recipe.importYaml,
        recipeSource: recipe.enableOnboarding ? recipe.recipeId : undefined
      })];
    }),
    mergeMap((actions) => Array.isArray(actions) ? actions : [actions]),
    filter((action) => !!action)
  ));

  // Effect 2: Handle new recipe system import after recipe is loaded
  private _onNewRecipeSystemImport$ = createEffect(() => this._queueImportWithClientId$.pipe(
    mergeMap((action) => {
      try {
        const recipeData = JSON.parse(action.nonHaRecipeId || '{}');

        // Handoff path is owned by _onQueueProjectImport$ above (which dispatches
        // consumeRecipeHandoff). Skip here so we don't fire a parallel projectImport
        // missing the zagentConfig meta — the consume fan-out re-dispatches
        // queueProjectImport without `handoff`, and that pass lands here normally.
        if (recipeData.handoff) {
          return [];
        }

        if (recipeData.recipeSlug && recipeData.environmentName) {

          if (recipeData.type === 'github') {
            // GitHub recipe - wait for GitHub recipe to be available, then import
            const githubUrl = recipeData.githubUrl;
            if (!githubUrl) return [];

            return this._store.pipe(
              select(recipeBaseState.selectGitHubRecipeByUrl(githubUrl)),
              filter((githubRecipe) => !!githubRecipe),
              take(1),
              withLatestFrom(this._store.pipe(select(selectZagentConfigMap))),
              map(([ githubRecipe, zagentConfigMap ]) => {
                // Find the specific YAML by environment name or filename
                let targetYaml = githubRecipe.yamls.find(yaml =>
                  yaml.environment === recipeData.environmentName ||
                  yaml.name === recipeData.environmentName
                );

                // Fallback to default YAML if not found
                if (!targetYaml) {
                  targetYaml = githubRecipe.defaultYaml;
                }

                if (!targetYaml?.content) {
                  return undefined;
                }

                // Secret pause (§6 path 3): the deploy-confirm dialog is mounted
                // ONLY on the recipe detail page, so a secret-needing recipe
                // cannot be filled here (we're mid-redirect to /dashboard).
                // Route the now-authed user to the GitHub recipe detail with
                // deploy=true; the path-1 deep-link effect opens the dialog in
                // its real host and re-resolves zagentConfig from the map.
                if (scanNullSecrets(targetYaml.content).length > 0) {
                  const redirect = zefGo([ '/recipes', 'detail' ], {
                    github: encodeURIComponent(githubUrl),
                    yaml: targetYaml.environment || recipeData.environmentName,
                    deploy: 'true'
                  });

                  // Seed the ROOT pending-config slice (always registered) under
                  // the GitHub deep-link reader's key (`${githubRecipe.id}:${env key}`
                  // — recipe.effect.ts onGitHubRecipeDeployParam$) so the reopened
                  // dialog recovers control-plane settings across the redirect. The
                  // lazy zagentConfigMap can't be seeded from this root effect.
                  if (action.zagentConfig) {
                    return [
                      pendingDeployConfigActions.set({
                        recipeSlug: githubRecipe.id,
                        environmentKey: targetYaml.environment,
                        config: action.zagentConfig
                      }),
                      redirect
                    ];
                  }

                  return redirect;
                }

                // Prefer the action prop (set by handoff fan-out) over the
                // map lookup. Map only populates when /recipes was loaded
                // in this session — not the case after OAuth callback. The
                // map key matches the GitHub deep-link reader scheme
                // (`${githubRecipe.id}:${env key}`).
                const zagentConfig = action.zagentConfig
                  ?? zagentConfigMap[`${githubRecipe.id}:${targetYaml.environment}`];

                return projectImport({
                  data: {
                    yaml: targetYaml.content,
                    recipeSource: this.#extractRecipeSource(githubRecipe.sourceUrl),
                    recipeSourceUrl: undefined  // GitHub recipes explicitly excluded
                  },
                  meta: { zagentConfig }
                });
              }),
              // Flatten: the map returns an Action, an Action[] (seed + redirect),
              // or undefined — emit individual actions so NgRx dispatches each.
              mergeMap((result) => Array.isArray(result) ? result : (result ? [ result ] : []))
            );
          } else {
            // Strapi recipe - existing logic
            return this._store.pipe(
              select(recipeBaseState.selectRecipeBySlug(recipeData.recipeSlug)),
              filter((recipe) => !!recipe),
              take(1),
              withLatestFrom(this._store.pipe(select(selectZagentConfigMap))),
              map(([ recipe, zagentConfigMap ]) => {
                const environment = recipe.sourceData?.environments?.find(env =>
                  env.projectName === recipeData.environmentName ||
                  env.name === recipeData.environmentName ||
                  env.key === recipeData.environmentName
                );
                if (!environment?.import) {
                  return undefined;
                }

                // Secret pause (§6 path 3): the deploy-confirm dialog is mounted
                // ONLY on the recipe detail page. Route the now-authed user to
                // the native recipe detail with deploy=true; the path-1
                // deep-link effect opens the dialog in its real host and
                // re-resolves zagentConfig from the map. `environment` here is a
                // RecipeEnvironmentParsed, so `.key` matches what path 1 expects
                // (queryParams.environment → envMap[key]).
                if (scanNullSecrets(environment.import).length > 0) {
                  const redirect = zefGo([ '/recipes', recipeData.recipeSlug ], {
                    environment: environment.key,
                    deploy: 'true'
                  });

                  // Seed the ROOT pending-config slice under the NATIVE deep-link
                  // reader's key (`${recipe.slug}:${activeEnv.key}`) so control-plane
                  // settings survive the redirect (the lazy zagentConfigMap can't be
                  // seeded from this root effect).
                  if (action.zagentConfig) {
                    return [
                      pendingDeployConfigActions.set({
                        recipeSlug: recipeData.recipeSlug,
                        environmentKey: environment.key,
                        config: action.zagentConfig
                      }),
                      redirect
                    ];
                  }

                  return redirect;
                }

                // Extract github repo from first service if available, fallback to recipe.source
                const githubRepo = environment.services?.[0]?.gitRepo?.split('@')[0];
                const recipeSource = this.#extractRecipeSource(githubRepo) || this.#extractRecipeSource(recipe.source);
                // Prefer the action prop (set by handoff fan-out) over the
                // map lookup. Map only populates when /recipes was loaded
                // in this session — not the case after OAuth callback.
                const zagentConfig = action.zagentConfig
                  ?? zagentConfigMap[`${recipeData.recipeSlug}:${recipeData.environmentName}`];

                return projectImport({
                  data: {
                    yaml: environment.import,
                    recipeSource,
                    // Use pre-computed recipeSourceUrl from registration, or compute it
                    recipeSourceUrl: recipeData.recipeSourceUrl || buildRecipeSourceUrl(
                      recipeData.recipeSlug,
                      recipeData.environmentName
                    )
                  },
                  meta: { zagentConfig }
                });
              }),
              // Flatten: the map returns an Action, an Action[] (seed + redirect),
              // or undefined — emit individual actions so NgRx dispatches each.
              mergeMap((result) => Array.isArray(result) ? result : (result ? [ result ] : []))
            );
          }
        }
      } catch (e) {
        // Not JSON, ignore
      }
      return [];
    }),
    mergeMap((result) => Array.isArray(result) ? result : [result]),
    filter((action) => !!action)
  ));


  private _onHandleStoreUserDataSuccess$ = createEffect(() => this._actions$.pipe(
    ofType(storeUserDataSuccess),
    filter(({ refresh }) => refresh === true),
    // map(() => zefGo([ '/dashboard' ])),
    tap(({ redirectTo }) => setTimeout(() => window.location.replace(redirectTo || '/dashboard/projects')))
  ), { dispatch: false });

  // TEMP: periodically kill websocket to make sure it works 🤷‍♂️
  // private _websocketKiller$ = createEffect(() => this._userEntity.activeClientId$.pipe(
  //   switchMap(() => interval(55000).pipe(
  //     map(() => zefWebsocketClosed()),
  //     takeUntil(this._onAnyAuthClear$.pipe(take(1)))
  //   ))
  // ));

  // Recipe-handoff capture/consume on fresh login was moved into
  // RecipeHandoffBaseEffect (route-entry stash + single deferred consume on
  // storeUserDataSuccess), which fixes the 2FA/passkey/multi-org mis-fires.
  // The already-authed in-app path is still consumed eagerly via
  // _onQueueProjectImport$ below.

  #extractRecipeSource(githubRepo?: string): string | undefined {
    if (!githubRepo) return undefined;
    const match = githubRepo.match(/github\.com\/(.+?)(?:\.git)?$/);
    return match?.[1];
  }

  constructor(
    private _actions$: Actions<any>,
    private _store: Store<AppState>,
    private _userEntity: UserEntity,
    private _wService: ZefWebsocketService,
    private _noola: Noola,
    private _tokenStorage: AuthTokenStorageService
  ) { }
}
